import React from 'react';
import {
  ArrowRight,
  Brain,
  Building2,
  ClipboardCheck,
  FileText,
  Globe2,
  Layers,
  ListChecks,
  Phone,
  Rocket,
  Search,
  TrendingUp,
  UserCheck,
  Users,
  Video,
} from 'lucide-react';
import { Button } from '@/Components/ui/button';
import Header from '../Header&Footer/Header';
import Footer from '../Header&Footer/Footer';
import useSEO from '../HomePage/useSEO';
import ScrollReveal from '../HomePage/ScrollReveal';
import CaseStudyProof, { caseStudyProofs } from '../../../Blog/CaseStudyProof';
import {
  CapabilityGrid,
  CONTACT_PATH,
  DEMO_PATH,
  IndustryCta,
  IndustryFlowStyles,
  IndustryWorkflow,
  Section,
  SectionIntro,
} from '../Industries/industryUi';

const AUDIENCES = [
  {
    audience: 'For Startups',
    title: 'Hire your first 50 people.',
    detail: 'Help founders and lean teams move from role creation to qualified candidates without building a large recruiting team.',
    icon: Rocket,
    href: '/solutions/startup-hiring',
    cta: 'See startup hiring',
  },
  {
    audience: 'For Growing Companies',
    title: 'Scale recruitment without scaling recruiters.',
    detail: 'Support increasing hiring demand with AI-assisted screening, assessment, interviews and recruiter workflows.',
    icon: TrendingUp,
    href: '/solutions/ai-recruitment',
    cta: 'See AI recruitment',
  },
  {
    audience: 'For Enterprises',
    title: 'Standardize hiring across teams and geographies.',
    detail: 'Create more consistent hiring workflows while keeping human teams in control of final decisions.',
    icon: Building2,
    href: '/solutions/enterprise',
    cta: 'See enterprise hiring',
  },
  {
    audience: 'For Recruitment Agencies',
    title: 'Multiply recruiter productivity.',
    detail: 'Use AI to handle repetitive hiring work while recruiters focus on candidates, clients and decisions.',
    icon: Users,
    href: '/solutions/rpo',
    cta: 'See RPO services',
  },
  {
    audience: 'For GCCs',
    title: 'Build and scale your India team.',
    detail: 'Support leadership hiring, core team building, functional expansion and continued hiring growth.',
    icon: Globe2,
    href: '/solutions/gcc-hiring',
    cta: 'See GCC hiring',
  },
  {
    audience: 'For High-Volume Hiring',
    title: 'Screen thousands without creating a recruiter bottleneck.',
    detail: 'Use AI-assisted screening, assessments and interviews to handle larger applicant volumes more efficiently.',
    icon: Layers,
    href: '/solutions/high-volume-hiring',
    cta: 'See high-volume hiring',
  },
];

const LEFT_NEEDS = ['Startups', 'Recruitment agencies', 'GCCs'];
const RIGHT_NEEDS = ['Growing companies', 'Enterprises', 'High-volume hiring'];

const FLOW = [
  { title: 'Source', detail: 'Find people who fit the role.', icon: Search },
  { title: 'Screen', detail: 'Review resumes and early answers before a recruiter spends time.', icon: FileText },
  { title: 'Assess', detail: 'Use the assessment that matches the job.', icon: ClipboardCheck },
  { title: 'Interview', detail: 'Keep a structured interview the team can review.', icon: Video },
  { title: 'Shortlist', detail: 'A person compares the records.', icon: ListChecks },
  { title: 'Hire', detail: 'The hiring team decides.', icon: UserCheck },
];

const AI_WORK = [
  'Sourcing support',
  'Screening',
  'Assessments',
  'Repetitive interviewing',
  'Candidate evidence',
];

const HUMAN_WORK = [
  'Judgment',
  'Recruiter review',
  'Hiring manager conversation',
  'Final hiring decision',
];

const CAPABILITIES = [
  {
    title: 'AI Sourcing & Screening',
    helps: 'Who enters the pipeline, and how the resume fits the role.',
    href: '/hirewithus/ai-sourcing-screening',
    icon: FileText,
  },
  {
    title: 'AI Call Screening',
    helps: 'How a candidate answers role questions on a structured call.',
    href: '/hirewithus/ai-call-screening',
    icon: Phone,
  },
  {
    title: 'Coding Assessment',
    helps: 'Whether technical candidates can do the work.',
    href: '/hirewithus/coding-assessment',
    icon: ListChecks,
  },
  {
    title: 'Screening Assessment',
    helps: 'How the resume scores against the job description.',
    href: '/hirewithus/screening-assessment',
    icon: ClipboardCheck,
  },
  {
    title: 'Functional Assessment',
    helps: 'Behaviour, communication, and role-based skills.',
    href: '/hirewithus/functional-assessment',
    icon: Brain,
  },
  {
    title: 'Virtual Hiring',
    helps: 'A structured AI interview the team can review.',
    href: '/hirewithus/virtualhiring',
    icon: Video,
  },
];

export default function SolutionsHub() {
  useSEO({
    title: 'AI Recruitment Solutions | SearchJob.ai',
    description:
      'AI-powered recruitment solutions for startups, growing companies, enterprises, recruitment agencies, GCCs and high-volume hiring teams.',
    canonical: 'https://searchjob.ai/solutions',
    ogImage: 'https://searchjob.ai/og-image.png',
  });

  return (
    <div className="min-h-screen overflow-x-hidden bg-background">
      <Header />
      <main className="pt-16">
        <Hero />
        <ScrollReveal>
          <AudienceGrid />
        </ScrollReveal>
        <ScrollReveal>
          <SharedLayer />
        </ScrollReveal>
        <ScrollReveal>
          <HumanLoop />
        </ScrollReveal>
        <ScrollReveal>
          <Capabilities />
        </ScrollReveal>
        <ScrollReveal>
          <Proof />
        </ScrollReveal>
        <FinalCta />
      </main>
      <Footer />
      <IndustryFlowStyles />
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden bg-background">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute right-0 top-0 h-64 w-64 rounded-full bg-primary/5 blur-3xl" />
      </div>
      <div className="mx-auto grid max-w-7xl items-start gap-12 px-4 pt-10 pb-16 sm:px-6 sm:pt-12 lg:grid-cols-[1.05fr_0.95fr] lg:px-8 lg:pt-12 lg:pb-20">
        <div>
          <p className="mb-5 inline-flex items-center rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
            Solutions
          </p>
          <h1 className="mb-6 text-4xl font-bold leading-[1.08] tracking-[-0.02em] text-foreground sm:text-5xl lg:text-6xl">
            One AI Workforce. Every Hiring Need.
          </h1>
          <p className="mb-8 max-w-2xl text-lg leading-relaxed text-muted-foreground sm:text-xl">
            One platform for startups, growing companies, enterprises, recruitment agencies, GCCs and high-volume hiring teams.
          </p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button
              asChild
              size="lg"
              className="!h-12 !rounded-full !bg-primary px-8 text-base font-semibold !text-primary-foreground hover:!bg-primary/90"
            >
              <a href={DEMO_PATH}>
                Book a Hiring Consultation
                <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden="true" />
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="!h-12 !rounded-full bg-card/70 px-8 text-base font-semibold"
            >
              <a href="#audience-solutions">Explore Solutions</a>
            </Button>
          </div>
        </div>
        <HeroMap />
      </div>
    </section>
  );
}

function HeroMap() {
  return (
    <div className="rounded-3xl border border-primary bg-primary/5 p-5 shadow-sm sm:p-6" aria-hidden="true">
      <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Who it is for</p>
      <div className="grid items-center gap-3 lg:grid-cols-[1fr_9.5rem_1fr]">
        <ul className="grid gap-2">
          {LEFT_NEEDS.map((name) => (
            <NeedChip key={name} name={name} />
          ))}
        </ul>
        <div className="rounded-2xl border border-primary/30 bg-primary/5 px-3 py-5 text-center">
          <p className="text-sm font-semibold text-foreground">SearchJob.ai</p>
          <p className="mt-1 text-xs text-muted-foreground">One hiring layer</p>
        </div>
        <ul className="grid gap-2">
          {RIGHT_NEEDS.map((name) => (
            <NeedChip key={name} name={name} />
          ))}
        </ul>
      </div>
    </div>
  );
}

function NeedChip({ name }) {
  return (
    <li className="rounded-xl border border-primary/20 bg-background px-3 py-2.5 text-sm font-medium text-foreground">
      {name}
    </li>
  );
}

function AudienceGrid() {
  return (
    <Section id="audience-solutions" muted>
      <SectionIntro
        eyebrow="Find your path"
        title="Which solution fits this hiring need?"
        copy="Choose the team you are hiring for. A detail page opens only when that page already exists."
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {AUDIENCES.map((item) => (
          <AudienceCard key={item.audience} item={item} />
        ))}
      </div>
    </Section>
  );
}

function AudienceCard({ item }) {
  const body = (
    <>
      <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl border border-primary/30 bg-primary/10 text-primary transition-colors duration-200 group-hover:border-primary">
        <item.icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <p className="text-xs font-semibold uppercase tracking-wider text-primary">{item.audience}</p>
      <h3 className="mt-2 text-base font-semibold text-foreground">{item.title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.detail}</p>
    </>
  );

  if (!item.href) {
    return (
      <article className="flex min-h-11 flex-col rounded-2xl border border-primary bg-primary/5 p-5">
        {body}
        <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Coming soon</p>
      </article>
    );
  }

  return (
    <a
      href={item.href}
      className="group flex min-h-11 flex-col rounded-2xl border border-primary bg-primary/5 p-5 transition duration-200 hover:-translate-y-0.5 hover:border-primary hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary motion-reduce:transition-none motion-reduce:hover:translate-y-0 motion-reduce:hover:shadow-none"
    >
      {body}
      <span className="mt-4 inline-flex items-center text-sm font-semibold text-primary">
        {item.cta}
        <ArrowRight className="ml-1 h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 motion-reduce:transition-none motion-reduce:group-hover:translate-x-0" aria-hidden="true" />
      </span>
    </a>
  );
}

function SharedLayer() {
  return (
    <Section>
      <SectionIntro
        eyebrow="One platform"
        title="Different hiring needs. One shared recruitment operating layer."
        copy="The same platform can support sourcing through hire. The steps a team uses still depend on the role."
      />
      <IndustryWorkflow steps={FLOW} />
      <ul className="mt-6 flex flex-wrap gap-2">
        {AUDIENCES.map((item) => (
          <li key={item.audience}>
            <span className="inline-flex min-h-11 items-center rounded-full border border-primary/30 bg-primary/5 px-4 text-sm font-medium text-foreground">
              {item.audience.replace('For ', '')}
            </span>
          </li>
        ))}
      </ul>
    </Section>
  );
}

function HumanLoop() {
  return (
    <Section muted>
      <SectionIntro
        eyebrow="AI and people"
        title="AI agents handle the repetitive work. Your people make the hiring decisions."
        copy="SearchJob.ai collects evidence. It does not make the hiring decision."
      />
      <div className="grid gap-4 md:grid-cols-2">
        <WorkList title="AI handles" items={AI_WORK} />
        <WorkList title="Your team handles" items={HUMAN_WORK} />
      </div>
    </Section>
  );
}

function WorkList({ title, items }) {
  return (
    <article className="rounded-2xl border border-primary bg-primary/5 p-5">
      <h3 className="text-base font-semibold text-foreground">{title}</h3>
      <ul className="mt-4 space-y-2">
        {items.map((item) => (
          <li key={item} className="rounded-xl border border-border bg-background px-3 py-2.5 text-sm text-foreground">
            {item}
          </li>
        ))}
      </ul>
    </article>
  );
}

function Capabilities() {
  return (
    <Section>
      <SectionIntro
        eyebrow="Platform capabilities"
        title="Products already on SearchJob.ai."
        copy="These are the tools behind the solutions. This page is about who they are for."
      />
      <CapabilityGrid items={CAPABILITIES} />
    </Section>
  );
}

function Proof() {
  return (
    <Section muted>
      <SectionIntro
        eyebrow="Customer proof"
        title="Stories already published on SearchJob.ai."
        copy="These are the approved case studies. They are not assigned to an audience page that does not exist yet."
      />
      <CaseStudyProof items={[caseStudyProofs.manest, caseStudyProofs.grandGaming, caseStudyProofs.kinzy]} />
      <a
        href="/case-studies"
        className="mt-6 inline-flex min-h-11 items-center text-sm font-semibold text-primary underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      >
        All case studies
        <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
      </a>
    </Section>
  );
}

function FinalCta() {
  return (
    <IndustryCta
      title="Find the right SearchJob solution for your hiring plan."
      copy="Tell us what you are hiring for, and we'll show you the right combination of AI and recruitment support."
      primaryLabel="Book a Hiring Consultation"
      primaryHref={DEMO_PATH}
      secondaryLabel="Contact Us"
      secondaryHref={CONTACT_PATH}
    />
  );
}
